import type { CloseReason, Runtime, StreamState } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { useEffect, useRef, useState } from "react";
import type { LiveContext } from "./session.ts";

/**
 * FR-015-003, `contracts/screens.md` § Shell data: the workspace shell's one event stream, opened
 * beside the shell data and closed on a workspace switch, on unmount and on sign-out, returning the
 * stream machine's state for the connection indicator. The engine (`openStream.ts`) and the platform's
 * tab coordination it shares the stream through are lazy chunks loaded together as the shell mounts, so
 * the authenticated entry carries only this hook (FR-015-120); the cost is those chunks' requests on a
 * first visit before the connection starts. A coordination that fails to load leaves the tab its own
 * stream, as a browser without one does.
 */
export function useWorkspaceStream(runtime: Runtime, ws: string, enabled: boolean, actorId: string | null, workspaceId: string | null): StreamState {
  const { t } = useI18n();
  const [state, setState] = useState<StreamState>({ kind: "idle" });
  const live = useRef<LiveContext>({ actorId, workspaceId, t });
  const mounted = useRef(true);

  useEffect(() => {
    live.current.actorId = actorId;
    live.current.workspaceId = workspaceId;
    live.current.t = t;
  }, [actorId, workspaceId, t]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let close: ((reason: CloseReason) => void) | null = null;
    let cancelled = false;
    const tabs = runtime.platform.loadTabs?.().catch(() => null) ?? Promise.resolve(null);
    void Promise.all([import("./openStream.ts"), tabs]).then(([{ openWorkspaceStream }, coordination]) => {
      if (!cancelled) close = openWorkspaceStream(runtime, ws, live.current, setState, coordination);
    });
    return () => {
      cancelled = true;
      close?.(mounted.current ? "workspace-switch" : "unmounted");
    };
  }, [runtime, ws, enabled]);

  return state;
}
